import { faStar } from "@fortawesome/free-solid-svg-icons";
import { Amary, ContentBox, CourseContent, CourseSummary, MaskProps } from "./Tutorial";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { mockNegotiationControl } from "../../services/negotiationBridge";

export interface GeneralCourse {
  summary: CourseSummary;
  content: CourseContent;
}

const AmaryDefault: Amary = {
  id: "normal",
  position: { x: "calc(100% - 220px)", y: "calc(100% - 300px)" },
  speed: 500,
  extraCSS: {
    opacity: 1,
  },
};

// While the post dialog is open its footer, with Fechar, reaches the bottom
// right corner where Amary stands, so she moves left, over the media stage.
const AmaryBesideDialog: Amary["position"] = { x: "20px", y: "calc(100% - 300px)" };

const AmaryInitialDefault: Amary = {
  ...AmaryDefault,
  delayedCSS: {
    600: {
      opacity: 1,
    },
  },
};

/**
 * Sidebar nav items remain clickable during the entire tutorial so the user
 * can jump around freely instead of being trapped in the current chapter.
 * The tutorial waits on `expectedRoute` to advance, so a stray click on
 * another nav item just navigates without skipping the step.
 */
const SIDEBAR_NAV_IDS = [
  "rootNavbarPainel",
  "rootNavbarAds",
  "rootNavbarOrg",
  "rootNavbarConfig",
  "rootNavbarSupport",
];

const MaskDefault: MaskProps = {
  type: undefined,
  elementId: "",
  speed: 500,
  unblockedElementIds: SIDEBAR_NAV_IDS,
};

const ContentBoxDefault: ContentBox = {
  position: { x: "230px", y: "calc(100% - 70px)" },
  animationSpeed: 300,
  text: "",
  speechSpeed: 40,
  extraCSS: {
    width: "calc(100vw - 500px)",
    height: "60px",
    boxShadow: "0 0 5px 1px var(--brand-12)",
  },
  continueTextPos: "top",
  onReady: () => {},
  speechFast: false,
  delay: 750,
  triggerNextType: "anywhere" as "anywhere" | "mask-only",
};

const ContentBoxInitialDefault: ContentBox = {
  ...ContentBoxDefault,
  text: `Prazer. Eu sou a Amary, sua gerente virtual. Sim, eu mando aqui. Vamos começar?`,
  extraCSS: {
    width: 0,
    height: 0,
  },
  delay: 750,
  delayedCSS: {
    100: {
      width: "calc(100vw - 500px)",
      height: "60px",
    },
    400: {
      boxShadow: "0 0 5px 1px var(--brand-12)",
    },
  },
};

export const courses: { [key: string]: GeneralCourse } = {
  welcome: {
    summary: {
      id: "welcome",
      title: "Bem-Vindo!",
      description: "Conheça brevemente as funcionalidades básicas",
      totalChapters: 1,
      preview: <FontAwesomeIcon icon={faStar} />,
      skipInDev: false,
    },
    content: {
      id: "welcome",
      chapters: [
        {
          Amary: { ...AmaryInitialDefault, id: "happy" },
          Mask: MaskDefault,
          ContentBox: ContentBoxInitialDefault,
        },
        {
          Amary: {
            ...AmaryDefault,
          },
          Mask: {
            ...MaskDefault,
            type: "horizontal-pill",
            elementId: "rootNavbarPainel",
            widthDiff: -100,
            posXDiff: 0,
            // navigate("/") goes through the LandingPage loader which
            // redirects authenticated users to /painel.
            expectedRoute: "/painel",
          },
          ContentBox: {
            ...ContentBoxDefault,
            triggerNextType: "mask-only",
            text: `Primeira lição: o painel. Clica ali no navegador onde está destacado. Vai, eu espero.`,
          },
        },
        {
          Amary: {
            ...AmaryDefault,
            id: "analysis",
          },
          Mask: {
            ...MaskDefault,
          },
          ContentBox: {
            ...ContentBoxDefault,
            text: `Esse aqui é o painel. Onde os números falam e as desculpas morrem. Fique de olho nos gráficos.`,
          },
        },
        {
          Amary: {
            ...AmaryDefault,
            id: "analysis",
          },
          Mask: {
            ...MaskDefault,
          },
          ContentBox: {
            ...ContentBoxDefault,
            text: `Esses gráficos mostram como seus anúncios estão performando: impressões, engajamento, custo. Tudo que importa pra saber se o dinheiro está bem investido.`,
          },
        },
        {
          Amary: {
            ...AmaryDefault,
          },
          Mask: {
            ...MaskDefault,
            type: "horizontal-pill",
            elementId: "rootNavbarAds",
            widthDiff: -100,
            posXDiff: 0,
            // /anuncios redirects to /anuncios/radio (or /tv, /atrio)
            // depending on permissions; accept any sub-route.
            expectedRoute: (p) => p.startsWith("/anuncios"),
          },
          ContentBox: {
            ...ContentBoxDefault,
            text: `Bom, chega de admirar gráfico. Clica ali na seção de anúncios. É onde a coisa fica interessante.`,
            triggerNextType: "mask-only",
          },
        },
        {
          Amary: {
            ...AmaryDefault,
            id: "happy",
          },
          Mask: {
            ...MaskDefault,
          },
          ContentBox: {
            ...ContentBoxDefault,
            text: `Seção de anúncios! Aqui é onde a mágica acontece. Crie campanhas, gerencie o que está rodando, e veja o retorno do investimento.`,
          },
        },
        {
          Amary: {
            ...AmaryDefault,
            id: "serious",
          },
          Mask: {
            ...MaskDefault,
          },
          ContentBox: {
            ...ContentBoxDefault,
            text: `E mais: dá pra controlar o custo por segmento e definir limites de gasto. Porque dinheiro não nasce em árvore, né?`,
          },
        },
        {
          Amary: {
            ...AmaryDefault,
          },
          Mask: {
            ...MaskDefault,
            type: "horizontal-pill",
            elementId: "rootNavbarOrg",
            widthDiff: -100,
            posXDiff: 0,
            // /organizacao redirects to /organizacao/info (or other
            // subpages) depending on permissions; accept any sub-route.
            expectedRoute: (p) => p.startsWith("/organizacao"),
          },
          ContentBox: {
            ...ContentBoxDefault,
            text: `Muito bem, estou impressionada. Agora clica na seção de organização ali.`,
            triggerNextType: "mask-only",
          },
        },
        {
          Amary: {
            ...AmaryDefault,
          },
          Mask: {
            ...MaskDefault,
          },
          ContentBox: {
            ...ContentBoxDefault,
            text: `Seção de organização. Aqui você gerencia dados cadastrais, colaboradores e métodos de pagamento. A burocracia necessária pra tudo funcionar.`,
            triggerNextType: "mask-only",
          },
        },
        {
          Amary: {
            ...AmaryDefault,
            id: "serious",
          },
          Mask: {
            ...MaskDefault,
          },
          ContentBox: {
            ...ContentBoxDefault,
            text: `E atenção: cada colaborador tem permissões específicas. Sem permissão, sem acesso. Segurança não é opcional.`,
          },
        },
        {
          Amary: {
            ...AmaryDefault,
            id: "tired",
          },
          Mask: {
            ...MaskDefault,
            type: "horizontal-pill",
            elementId: "rootNavbarConfig",
            widthDiff: -100,
            posXDiff: 0,
            expectedRoute: "/configuracoes",
          },
          ContentBox: {
            ...ContentBoxDefault,
            text: `Sim, eu sei que tutorial é chato. Mas prefere ligar pro suporte? Clica nas configurações ali.`,
            triggerNextType: "mask-only",
          },
        },
        {
          Amary: {
            ...AmaryDefault,
          },
          Mask: {
            ...MaskDefault,
          },
          ContentBox: {
            ...ContentBoxDefault,
            text: `Configurações. Edite seu perfil, troque sua senha, ajuste as preferências da conta. Básico, mas essencial.`,
          },
        },
        {
          Amary: {
            ...AmaryDefault,
          },
          Mask: {
            ...MaskDefault,
            type: "horizontal-pill",
            elementId: "rootNavbarSupport",
            widthDiff: -100,
            posXDiff: 0,
            expectedRoute: "/suporte",
          },
          ContentBox: {
            ...ContentBoxDefault,
            text: `E se um dia se perder... o suporte está sempre ali. Mas tenta resolver comigo primeiro, tá?`,
            triggerNextType: "mask-only",
          },
        },
        {
          Amary: {
            ...AmaryDefault,
            id: "fire",
          },
          Mask: {
            ...MaskDefault,
          },
          ContentBox: {
            ...ContentBoxDefault,
            text: `ISSO! Viu como é fácil quando me escuta? Agora vai lá explorar a plataforma. E se precisar, eu estou aqui. Sempre.`,
          },
        },
      ],
    },
  },
  negotiate: {
    summary: {
      id: "negotiate",
      title: "Negociação no Átrio",
      description: "Aprenda a utilizar o sistema de negociação no Átrio",
      totalChapters: 1,
      preview: <FontAwesomeIcon icon={faStar} />,
      skipInDev: true,
    },
    content: {
      id: "negotiate",
      chapters: [
        {
          Amary: { ...AmaryInitialDefault, id: "dialogue" },
          Mask: MaskDefault,
          ContentBox: ContentBoxInitialDefault,
        },
        {
          Amary: {
            ...AmaryDefault,
          },
          Mask: {
            ...MaskDefault,
            type: "horizontal-pill",
            elementId: "rootNavbarNegotiate",
            widthDiff: -100,
            posXDiff: 0,
          },
          ContentBox: {
            ...ContentBoxDefault,
            triggerNextType: "mask-only",
            text: `Hora de negociar. Clica em "negociar" no painel lateral. Aqui é onde o jogo começa.`,
          },
        },
        {
          Amary: {
            ...AmaryDefault,
            id: "dialogue",
          },
          Mask: {
            ...MaskDefault,
          },
          ContentBox: {
            ...ContentBoxDefault,
            text: `Bem-vindo ao Átrio. Arena de negociações. Aqui empresa compete contra empresa. Sobrevive quem lança melhor.`,
          },
        },
        {
          Amary: {
            ...AmaryDefault,
            id: "serious",
          },
          Mask: {
            ...MaskDefault,
          },
          ContentBox: {
            ...ContentBoxDefault,
            text: `As negociações abrem em períodos específicos do dia. Perdeu o horário? Perdeu a rodada. Fique atento.`,
          },
        },
        {
          Amary: {
            ...AmaryDefault,
            id: "happy",
          },
          Mask: {
            ...MaskDefault,
          },
          ContentBox: {
            ...ContentBoxDefault,
            text: `Olha a sorte! O mercado acabou de abrir uma nova rodada. Momento perfeito. Vamos aproveitar.`,
            onReady: () => {
              mockNegotiationControl.setMarketPeriod("negotiate");
            },
          },
        },
        {
          Amary: {
            ...AmaryDefault,
            id: "analysis",
          },
          Mask: {
            ...MaskDefault,
          },
          ContentBox: {
            ...ContentBoxDefault,
            text: `Veja: o mercado gira em torno de uma listagem ranqueada de postagens dos usuários. Posição é tudo aqui.`,
          },
        },
        {
          Amary: {
            ...AmaryDefault,
          },
          Mask: {
            ...MaskDefault,
          },
          ContentBox: {
            ...ContentBoxDefault,
            text: `A posição é a ordem em que cada postagem aparece na linha de postagens. Mas atenção: usuários podem rolar do começo pro fim OU do fim pro começo. Quem está lá embaixo na ordem normal, vira o topo quando alguém inverte. Os extremos da classificação são privilegiados, dos dois lados.`,
          },
        },
        {
          Amary: {
            ...AmaryDefault,
            id: "dialogue",
          },
          Mask: {
            ...MaskDefault,
          },
          ContentBox: {
            ...ContentBoxDefault,
            text: `Essas postagens estão abertas para receber anúncios. A sua empresa quer estar nelas. As outras também. Entendeu o clima?`,
          },
        },
        {
          Amary: {
            ...AmaryDefault,
            id: "serious",
          },
          Mask: {
            ...MaskDefault,
          },
          ContentBox: {
            ...ContentBoxDefault,
            text: `Pra ganhar o espaço, sua empresa precisa ter o maior lance quando o mercado fechar. Simples assim. Quem paga mais, leva.`,
          },
        },
        {
          Amary: {
            ...AmaryDefault,
          },
          Mask: {
            ...MaskDefault,
          },
          ContentBox: {
            ...ContentBoxDefault,
            text: `Resumindo: isso aqui é um leilão. Empresas competem entre si pelos melhores espaços publicitários nas postagens. Estratégia é tudo.`,
          },
        },
        {
          Amary: {
            ...AmaryDefault,
          },
          Mask: {
            ...MaskDefault,
          },
          ContentBox: {
            ...ContentBoxDefault,
            text: `Pra facilitar sua vida, temos filtros e ordenações. Eu sei que você não quer ficar perdido no meio de centenas de postagens.`,
          },
        },
        {
          Amary: {
            ...AmaryDefault,
            id: "analysis",
          },
          Mask: {
            ...MaskDefault,
            type: "horizontal-pill",
            elementId: "hallShowEstimative",
            fitToVisibleChildren: true,
          },
          ContentBox: {
            ...ContentBoxDefault,
            text: `Essa estimativa separa os lances brutos do total efetivo. Se sua empresa tiver um prêmio de ofensiva, o desconto aparece aqui, mas o valor competitivo do lance continua igual para todas as empresas.`,
          },
        },
        {
          Amary: {
            ...AmaryDefault,
          },
          Mask: {
            ...MaskDefault,
            type: "horizontal-pill",
            elementId: "hallNegotiationBalance",
            fitToVisibleChildren: true,
          },
          ContentBox: {
            ...ContentBoxDefault,
            text: `Do lado do total fica o seu saldo disponível pra novos lances. Cada lance reserva o valor dele até a apuração. Quem tem permissão de Pagamentos também vê ali o botão Recarregar.`,
          },
        },
        {
          Amary: {
            ...AmaryDefault,
            id: "serious",
          },
          Mask: {
            ...MaskDefault,
            type: "horizontal-pill",
            elementId: "hallCountdownToFinishNegotiation",
            fitToVisibleChildren: true,
          },
          ContentBox: {
            ...ContentBoxDefault,
            text: `Esse contador mostra quanto tempo falta pro fechamento. Quando zerar, acabou. Sem segunda chance.`,
          },
        },
        {
          Amary: {
            ...AmaryDefault,
            id: "fire",
          },
          Mask: {
            ...MaskDefault,
            type: "horizontal-pill",
            elementId: "offensivePreviewContainer",
            fitToVisibleChildren: true,
          },
          ContentBox: {
            ...ContentBoxDefault,
            text: `Aqui fica a previsão dos seus pontos de ofensiva. Esse fogo não é só enfeite, é poder de classificação.`,
          },
        },
        {
          Amary: {
            ...AmaryDefault,
          },
          Mask: {
            ...MaskDefault,
          },
          ContentBox: {
            ...ContentBoxDefault,
            text: `Pontos de ofensiva são pontos internos (o simbolo de fogo) que sua organização acumula pra disputar posições na classificação. Mais tarde explico as vantagens de estar no topo!`,
          },
        },
        {
          Amary: {
            ...AmaryDefault,
          },
          Mask: {
            ...MaskDefault,
            type: "horizontal-pill",
            elementId: "hallFilterButtonsContainer",
            fitToVisibleChildren: true,
            // Pull the right edge in so the pill stops short of the
            // adjacent Turbo button — clicks there mid-tutorial would
            // be confusing.
            widthDiff: -50,
          },
          ContentBox: {
            ...ContentBoxDefault,
            text: `HITS lista as postagens das primeiras posições. FLOPS lista as últimas, que viram topo quando o usuário inverte a ordem. Disputar FLOPS é quase ficar em primeiro pelo outro lado. Estratégia esperta brinca dos dois lados.`,
          },
        },
        {
          Amary: {
            ...AmaryDefault,
          },
          Mask: {
            ...MaskDefault,
            type: "horizontal-pill",
            elementId: "hallChipsBetContainer",
            fitToVisibleChildren: true,
            // The pill's round ends add the chip height on top of the row's
            // width: pull them in so they trace the end chips, keep the
            // selected chip's glow, and stop short of the notices bell.
            widthDiff: -40,
          },
          ContentBox: {
            ...ContentBoxDefault,
            text: `Essas fichas definem o valor do lance. Escolha quanto quer apostar em cada jogada. Pense bem antes de clicar.`,
          },
        },
        {
          Amary: {
            ...AmaryDefault,
            id: "dialogue",
          },
          Mask: {
            ...MaskDefault,
          },
          ContentBox: {
            ...ContentBoxDefault,
            text: `Certo. Agora vem a parte boa: escolher uma postagem pra fazer um lance. Pense estrategicamente.`,
          },
        },
        {
          Amary: {
            ...AmaryDefault,
          },
          Mask: {
            ...MaskDefault,
          },
          ContentBox: {
            ...ContentBoxDefault,
            text: `As postagens aparecem como figuras elípticas na lista. As especiais ganham destaque com cards diferenciados. Difícil de ignorar.`,
          },
        },
        {
          Amary: {
            ...AmaryDefault,
            id: "analysis",
          },
          Mask: {
            ...MaskDefault,
          },
          ContentBox: {
            ...ContentBoxDefault,
            text: `Postagens especiais ficam em posições de maior visibilidade ou aumentam o multiplicador de ofensiva. Traduzindo: valem ouro.`,
          },
        },
        {
          Amary: {
            ...AmaryDefault,
            id: "fire",
          },
          Mask: {
            ...MaskDefault,
            type: "rectangle",
            elementId: "highlightCardPos2",
          },
          ContentBox: {
            ...ContentBoxDefault,
            triggerNextType: "mask-only",
            text: `Chega de teoria. Clica nessa postagem que aumenta o multiplicador de ofensiva. Bora fazer um lance!`,
          },
        },
        {
          Amary: {
            ...AmaryDefault,
            position: AmaryBesideDialog,
          },
          Mask: {
            ...MaskDefault,
          },
          ContentBox: {
            ...ContentBoxDefault,
            text: `Ao clicar, a postagem abre inteira: a mídia à esquerda e, à direita, quem lidera e quanto custa o seu próximo lance. Dá uma olhada antes de decidir onde colocar o dinheiro da empresa.`,
          },
        },
        {
          Amary: {
            ...AmaryDefault,
            position: AmaryBesideDialog,
            id: "happy",
          },
          Mask: {
            ...MaskDefault,
            type: "horizontal-pill",
            elementId: "chipPosDoBid2",
            // The bid button is a full-width pill 52px tall: take back the one
            // height the pill shape adds so the spotlight hugs the button.
            widthDiff: -52,
          },
          ContentBox: {
            ...ContentBoxDefault,
            triggerNextType: "mask-only",
            text: `Gostou? Então manda o lance! Clica em 'Dar lance' e mostra pra concorrência quem manda.`,
          },
        },
        {
          Amary: {
            ...AmaryDefault,
            position: AmaryBesideDialog,
            id: "tired",
          },
          Mask: {
            ...MaskDefault,
          },
          ContentBox: {
            ...ContentBoxDefault,
            text: `Pronto, sua empresa lidera essa postagem. Depois de um lance tem um tempo de espera antes do próximo, e o painel mostra quanto falta no lugar do botão. Paciência, jovem.`,
          },
        },
        {
          Amary: {
            ...AmaryDefault,
            position: AmaryBesideDialog,
          },
          Mask: {
            ...MaskDefault,
            type: "horizontal-pill",
            elementId: "cancelPostDetailsButton2",
            // A plain 44px pill: take back the one height the pill shape adds.
            widthDiff: -44,
            // The instruction below offers Esc as an alternative, and
            // PostDetails really does close on it — which unmounts this
            // very button. Without accepting the key here the chapter
            // waits forever for a click on an element that is gone.
            advanceOnKeys: ["Escape"],
          },
          ContentBox: {
            ...ContentBoxDefault,
            triggerNextType: "mask-only",
            text: `Clica em 'fechar' (ou aperta Esc) pra voltar e explorar outras postagens. O mercado é grande.`,
          },
        },
        {
          Amary: {
            ...AmaryDefault,
            id: "wife",
          },
          Mask: {
            ...MaskDefault,
          },
          ContentBox: {
            ...ContentBoxDefault,
            text: `Mas olha isso... outra empresa cobriu seu lance. A audácia! Isso não vai ficar assim. Vamos revidar mais rápido.`,
            onReady: () => {
              mockNegotiationControl.emitBid("mock-card-isika", {
                lastBidOrg: "org-bravo",
                lastBidDiff: 25,
              });
            },
          },
        },
        {
          Amary: {
            ...AmaryDefault,
            id: "fire",
          },
          Mask: {
            ...MaskDefault,
            type: "horizontal-pill",
            elementId: "setTurboMode",
            widthDiff: -40,
          },
          ContentBox: {
            ...ContentBoxDefault,
            triggerNextType: "mask-only",
            text: `Ativa o modo turbo! Com ele, você faz lances sem abrir detalhes. Velocidade é vantagem competitiva.`,
          },
        },
        {
          Amary: {
            ...AmaryDefault,
            id: "serious",
          },
          Mask: {
            ...MaskDefault,
          },
          ContentBox: {
            ...ContentBoxDefault,
            text: `Detalhe importante: modo turbo só funciona em postagens que sua empresa já viu o conteúdo antes. Sem atalhos.`,
          },
        },
        {
          Amary: {
            ...AmaryDefault,
            id: "dialogue",
          },
          Mask: {
            ...MaskDefault,
            type: "rectangle",
            elementId: "highlightCardPos2",
          },
          ContentBox: {
            ...ContentBoxDefault,
            triggerNextType: "mask-only",
            text: `Agora clica na postagem e cobre o lance rapidinho. Mostra pra eles como se faz.`,
          },
        },
        {
          Amary: {
            ...AmaryDefault,
            id: "analysis",
          },
          Mask: {
            ...MaskDefault,
          },
          ContentBox: {
            ...ContentBoxDefault,
            text: `Repara no total: é exatamente a soma dos seus lances. Enquanto sua empresa fica dentro da fatia justa da rodada, é isso que sai.`,
          },
        },
        {
          Amary: {
            ...AmaryDefault,
            id: "serious",
          },
          Mask: {
            ...MaskDefault,
          },
          ContentBox: {
            ...ContentBoxDefault,
            text: `O que muda isso é a taxa de monopólio: se sua empresa passar a segurar mais postagens do que a fatia justa, ela multiplica o valor dos seus lances, e quanto maior o domínio, maior o multiplicador. Equilíbrio é saudável, não é?`,
          },
        },
        {
          Amary: {
            ...AmaryDefault,
            id: "happy",
          },
          Mask: {
            ...MaskDefault,
          },
          ContentBox: {
            ...ContentBoxDefault,
            text: `Muito bem! Os leilões do Átrio usam o Saldo de Negociações pré-pago. Cada lance reserva seu valor máximo, a taxa de monopólio e a tarifa de transferência mostradas na prévia. O débito acontece na apuração. Rádio e TV continuam na cobrança semanal.`,
          },
        },
        {
          Amary: {
            ...AmaryDefault,
            id: "fire",
          },
          Mask: {
            ...MaskDefault,
          },
          ContentBox: {
            ...ContentBoxDefault,
            text: `E os pontos de ofensiva conquistados vão direto pro placar da sua organização. Cada ponto conta!`,
          },
        },
        {
          Amary: {
            ...AmaryDefault,
            id: "wife",
          },
          Mask: {
            ...MaskDefault,
          },
          ContentBox: {
            ...ContentBoxDefault,
            text: `Mas atenção: pontos de ofensiva são frágeis. Se sua organização não der NENHUM lance numa rodada, perde TUDO que acumulou. Não vacile.`,
          },
        },
        {
          Amary: {
            ...AmaryDefault,
          },
          Mask: {
            ...MaskDefault,
          },
          ContentBox: {
            ...ContentBoxDefault,
            // Prepaid consent is separate from the historical autobuy setting.
            text: `Nas configurações do Átrio, a opção 'Patrocinar o público menor de idade' começa desativada. Ao habilitá-la, sua empresa aceita uma compra adicional: manter o público menor de uma postagem vencida por uma empresa +18. Não são lances automáticos.`,
          },
        },
        {
          Amary: {
            ...AmaryDefault,
            id: "analysis",
          },
          Mask: {
            ...MaskDefault,
          },
          ContentBox: {
            ...ContentBoxDefault,
            text: `Para manter esse público, sua empresa precisa estar apta a anunciar para todas as idades, ter o modo habilitado e o maior lance entre as demais empresas aptas, com o valor ainda reservado no saldo. O sino ao lado dos valores dos lances reúne os avisos que continuam ativos.`,
          },
        },
        {
          Amary: {
            ...AmaryDefault,
          },
          Mask: {
            ...MaskDefault,
          },
          ContentBox: {
            ...ContentBoxDefault,
            text: `O líder no fechamento é o vencedor definitivo. Se uma devolução de Pix impedir o pagamento dele, não há promoção de outra empresa. O patrocínio válido do público menor, quando existir, passa a ser mostrado para todos.`,
          },
        },
        {
          Amary: {
            ...AmaryDefault,
            id: "laugh",
          },
          Mask: {
            ...MaskDefault,
          },
          ContentBox: {
            ...ContentBoxDefault,
            text: `O patrocínio do público menor é pago: sua empresa paga seu próprio lance, com o desconto mensal aplicável, sem taxa de monopólio nem tarifa de transferência. O sino mostra as posições e o total. O modo não pode mudar entre o fim do leilão e o resultado.`,
          },
        },
        {
          Amary: {
            ...AmaryDefault,
          },
          Mask: {
            ...MaskDefault,
            type: "rectangle",
            elementId: "negotiateTabPageRanking",
          },
          ContentBox: {
            ...ContentBoxDefault,
            triggerNextType: "mask-only",
            text: `Chega de papo. Vamos ver a classificação das empresas. Clica ali.`,
          },
        },
        {
          Amary: {
            ...AmaryDefault,
            id: "dialogue",
          },
          Mask: {
            ...MaskDefault,
            type: "rectangle",
            elementId: "rankingCompetition",
          },
          ContentBox: {
            ...ContentBoxDefault,
            text: `Aqui está a temporada mensal de ofensiva. O contador mostra quando a classificação fecha e os três prêmios que serão usados no próximo mercado que realmente tiver postagens.`,
          },
        },
        {
          Amary: {
            ...AmaryDefault,
            id: "fire",
          },
          Mask: {
            ...MaskDefault,
          },
          ContentBox: {
            ...ContentBoxDefault,
            text: `Quando a temporada fecha, o top 3 fica congelado. O prêmio é usado uma única vez no próximo mercado com postagens; depois desse mercado, todas as empresas voltam a zero e uma nova temporada começa.`,
          },
        },
        {
          Amary: {
            ...AmaryDefault,
            id: "fire",
          },
          Mask: {
            ...MaskDefault,
          },
          ContentBox: {
            ...ContentBoxDefault,
            text: `1º lugar recebe 50% OFF em cada lance vencedor e não paga taxa de monopólio. O lance bruto continua valendo para disputar a postagem; desconto, cobrança e divisão do valor só são aplicados se a empresa realmente vencer.`,
          },
        },
        {
          Amary: {
            ...AmaryDefault,
            id: "happy",
          },
          Mask: {
            ...MaskDefault,
          },
          ContentBox: {
            ...ContentBoxDefault,
            text: `2º lugar recebe 25% OFF e o 3º, 10% OFF. Os dois também ficam sem taxa de monopólio. Exemplo: lance bruto de R$100 com 50% OFF vira R$50 efetivos, e o split é calculado sobre esses R$50.`,
          },
        },
        {
          Amary: {
            ...AmaryDefault,
          },
          Mask: {
            ...MaskDefault,
            type: "rectangle",
            elementId: "negotiateTabPageHistory",
          },
          ContentBox: {
            ...ContentBoxDefault,
            triggerNextType: "mask-only",
            text: `Quase acabando. Clica no histórico pra gente dar uma olhada nas negociações passadas.`,
          },
        },
        {
          Amary: {
            ...AmaryDefault,
            id: "analysis",
          },
          Mask: {
            ...MaskDefault,
            type: "rectangle",
            elementId: "negotiationHistorySummary",
          },
          ContentBox: {
            ...ContentBoxDefault,
            text: `Aqui ficam todas as negociações passadas. No topo, o resumo: quantos ciclos ficaram registrados, quanto a empresa gastou de verdade e a ofensiva depois do último ciclo.`,
          },
        },
        {
          Amary: {
            ...AmaryDefault,
            id: "analysis",
          },
          Mask: {
            ...MaskDefault,
            type: "rectangle",
            elementId: "negotiationHistoryListContainer",
          },
          ContentBox: {
            ...ContentBoxDefault,
            text: `Cada linha é um ciclo: as posições conquistadas, a ofensiva e o custo efetivo. Abre um ciclo pra ver a conta completa e estuda seus movimentos pra melhorar os próximos.`,
          },
        },
        {
          Amary: {
            ...AmaryDefault,
          },
          Mask: {
            ...MaskDefault,
            type: "rectangle",
            elementId: "negotiateTabPageBack",
          },
          ContentBox: {
            ...ContentBoxDefault,
            triggerNextType: "mask-only",
            text: `Pra sair do painel de negociações, é só clicar em 'voltar'. Simples. Vai lá.`,
          },
        },
        {
          Amary: {
            ...AmaryDefault,
            id: "fire",
          },
          Mask: {
            ...MaskDefault,
          },
          ContentBox: {
            ...ContentBoxDefault,
            text: `Pronto! Agora você sabe negociar no Átrio como gente grande. Vai lá e conquista os melhores espaços pra sua organização. Eu estarei observando.`,
          },
        },
      ],
    },
  },
};
